/**
 * Forge · Cockpit di Aria — Autonomy OS
 * =====================================
 * Visualizza in tempo reale:
 *   - Score di autonomia globale (0-100) + 9 assi con progress bar
 *   - Universal Action Bus (registro azioni tipizzate)
 *   - Self-Planner proposals (accept/reject/done)
 *   - Permission Engine (classificatore live)
 *
 * Ogni riga di codice di questa pagina AUMENTA l'autonomia di Aria:
 * la rende visibile, misurabile, controllabile dal Founder.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ForgeNavBar } from "../universe/Universe";
import AriaHologram from "../../components/AriaHologram";
import { WindowManager, WindowToolbar, windowStore } from "../../cognitive_engine/WindowManager";
import ReasoningPanel from "./ReasoningPanel";
import "./forge-home.css";
import "./forge-cockpit.css";

const API = "" || "";

async function api(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
  });
  return r.json();
}

// ══════════════════════════════════════════════════════════
// AUTONOMY SCORE
// ══════════════════════════════════════════════════════════
const AXIS_LABELS = {
  project_understanding: "Comprensione del progetto",
  interface_control:     "Controllo dell'interfaccia",
  workspace_control:     "Controllo dei workspace",
  window_control:        "Controllo delle finestre",
  tool_control:          "Controllo degli strumenti",
  planning_capability:   "Capacità di pianificazione",
  self_repair:           "Capacità di auto-riparazione",
  explanation_capability:"Capacità di spiegazione",
  code_quality:          "Qualità del codice",
  generalization_readiness: "Riutilizzabilità (cognitive engine)",
};

function scoreColor(v) {
  if (v >= 80) return "#22c55e";
  if (v >= 60) return "#22d3ee";
  if (v >= 40) return "#fbbf24";
  return "#f87171";
}

function AutonomyGauge({ overall }) {
  const color = scoreColor(overall);
  const circ = 2 * Math.PI * 54;
  const offset = circ - (overall / 100) * circ;
  return (
    <div className="cockpit-gauge" data-testid="cockpit-gauge">
      <svg viewBox="0 0 128 128" width="200" height="200">
        <defs>
          <linearGradient id="gaugeGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={color}/>
            <stop offset="100%" stopColor="#a78bfa"/>
          </linearGradient>
        </defs>
        <circle cx="64" cy="64" r="54" fill="none"
                stroke="rgba(139,92,246,0.10)" strokeWidth="10"/>
        <circle cx="64" cy="64" r="54" fill="none"
                stroke="url(#gaugeGrad)" strokeWidth="10"
                strokeDasharray={circ} strokeDashoffset={offset}
                strokeLinecap="round"
                transform="rotate(-90 64 64)"
                style={{ transition: "stroke-dashoffset 1.4s cubic-bezier(0.16,1,0.3,1)" }}/>
      </svg>
      <div className="cockpit-gauge-value">
        <span className="cockpit-gauge-num" style={{ color }}>{overall}</span>
        <span className="cockpit-gauge-unit">/ 100</span>
        <span className="cockpit-gauge-label">AUTONOMIA</span>
      </div>
    </div>
  );
}

function AxisBar({ name, value }) {
  const color = scoreColor(value);
  return (
    <div className="cockpit-axis" data-testid={`cockpit-axis-${name}`}>
      <div className="cockpit-axis-head">
        <span className="cockpit-axis-name">{AXIS_LABELS[name] || name}</span>
        <span className="cockpit-axis-value" style={{ color }}>{value}</span>
      </div>
      <div className="cockpit-axis-bar">
        <div className="cockpit-axis-fill"
             style={{ width: `${value}%`, background: color, boxShadow: `0 0 10px ${color}` }}/>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PROPOSALS
// ══════════════════════════════════════════════════════════
const SEV_COLOR = { high: "#f87171", medium: "#fbbf24", low: "#a78bfa" };

function ProposalRow({ prop, onDecide }) {
  const c = SEV_COLOR[prop.severity] || "#94a3b8";
  return (
    <div className="cockpit-proposal" style={{ borderLeftColor: c }}
         data-testid={`cockpit-proposal-${prop.id}`}>
      <div className="cockpit-proposal-head">
        <span className="cockpit-proposal-sev" style={{ color: c, borderColor: c + "40" }}>
          {prop.severity}
        </span>
        <span className="cockpit-proposal-axis">{prop.axis}</span>
        <span className="cockpit-proposal-title">{prop.title}</span>
      </div>
      <div className="cockpit-proposal-detail">{prop.detail}</div>
      <div className="cockpit-proposal-actions">
        <button onClick={() => onDecide(prop.id, "accepted")}
                data-testid={`accept-${prop.id}`}
                className="accept">✓ Accetta</button>
        <button onClick={() => onDecide(prop.id, "done")}
                data-testid={`done-${prop.id}`}
                className="done">● Fatta</button>
        <button onClick={() => onDecide(prop.id, "rejected")}
                data-testid={`reject-${prop.id}`}
                className="reject">✕ Ignora</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// MODULE GARAGE (P0.5A)
// ══════════════════════════════════════════════════════════
function ModuleGarage({ modules, stats, onPromote }) {
  const [tab, setTab] = useState("core");
  const filtered = modules.filter((m) => m.level === tab);
  return (
    <section className="cockpit-panel" data-testid="cockpit-garage">
      <div className="cockpit-panel-title">
        <span className="forge-panel-eye"/> Module Garage
        <span className="forge-panel-sub">
          {stats?.total_modules || 0} moduli · patrimonio permanente del Cognitive Engine
        </span>
      </div>
      <div className="cockpit-garage-tabs">
        {["core", "founder", "project"].map((lvl) => (
          <button key={lvl}
                  className={`cockpit-garage-tab ${tab === lvl ? "active" : ""}`}
                  onClick={() => setTab(lvl)}
                  data-testid={`garage-tab-${lvl}`}>
            {lvl.toUpperCase()}
            <span className="count">{stats?.by_level?.[lvl]?.count ?? 0}</span>
          </button>
        ))}
      </div>
      <div className="cockpit-garage-list">
        {filtered.length === 0 && (
          <div className="cockpit-empty">Nessun modulo in {tab}.</div>
        )}
        {filtered.map((m) => (
          <div key={m.id} className="cockpit-module" data-testid={`garage-module-${m.id}`}>
            <div className="cockpit-module-head">
              <span className="cockpit-module-name">{m.name}</span>
              <code className="cockpit-module-id">{m.id}</code>
              <span className={`cockpit-module-maturity mt-${m.maturity}`}>{m.maturity}</span>
            </div>
            <div className="cockpit-module-desc">{m.description || "—"}</div>
            <div className="cockpit-module-meta">
              <span>origine: <b>{m.origin_project}</b></span>
              <span>cat: <b>{m.category}</b></span>
              <span>v{m.version}</span>
              <span>K: <b>{m.knowledge_value}</b></span>
              {(m.level !== "core") && (
                <button className="promote"
                        onClick={() => onPromote(m.id, m.level === "project" ? "founder" : "core")}
                        data-testid={`promote-${m.id}`}>
                  ↑ Promuovi a {m.level === "project" ? "founder" : "core"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════
// ACTIONS BUS
// ══════════════════════════════════════════════════════════
function ActionRow({ act, onExecute }) {
  return (
    <div className="cockpit-action" data-testid={`cockpit-action-${act.id}`}>
      <span className="cockpit-action-verb">{act.verb}</span>
      <span className="cockpit-action-kind">{act.kind}</span>
      <code className="cockpit-action-endpoint">{act.endpoint}</code>
      <span className={`cockpit-action-risk risk-${act.risk}`}>{act.risk}</span>
      <button className="cockpit-action-exec"
              onClick={() => onExecute && onExecute(act)}
              data-testid={`cockpit-action-exec-${act.id}`}
              title="Esegui (dry-run)">⚡</button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PROJECT RADAR (P0.1)
// ══════════════════════════════════════════════════════════
function ProjectRadar({ projects, onScan, scanningId }) {
  return (
    <section className="cockpit-panel" data-testid="cockpit-projects">
      <div className="cockpit-panel-title">
        <span className="forge-panel-eye"/> Project Radar
        <span className="forge-panel-sub">{projects.length} indicizzati · Cognitive Engine</span>
      </div>
      <div className="cockpit-projects-list">
        {projects.map((p) => (
          <div key={p.id} className="cockpit-project" data-testid={`cockpit-project-${p.id}`}>
            <div className="cockpit-project-head">
              <span className="cockpit-project-name">{p.name}</span>
              <code className="cockpit-project-id">{p.id}</code>
              {p.is_host && <span className="cockpit-project-host">HOST</span>}
            </div>
            <div className="cockpit-project-stack">
              {(p.stack || []).map((s, i) => (
                <span key={i} className="cockpit-project-tech">{s}</span>
              ))}
            </div>
            {p.last_scan_health && (
              <div className="cockpit-project-health">
                <span>files: <b>{p.last_scan_health.total_files}</b></span>
                <span>LOC: <b>{(p.last_scan_health.total_loc || 0).toLocaleString()}</b></span>
                <span>testid: <b>{p.last_scan_health.testid_total}</b></span>
                <span>doc%: <b>{p.last_scan_health.docstring_coverage_py}</b></span>
              </div>
            )}
            <button onClick={() => onScan(p.id)}
                    disabled={scanningId === p.id}
                    data-testid={`cockpit-scan-project-${p.id}`}
                    className="cockpit-project-scan-btn">
              {scanningId === p.id ? "Sto indicizzando…" : "🔎 Rescan"}
            </button>
          </div>
        ))}
        {projects.length === 0 && (
          <div className="cockpit-empty">
            Nessun progetto registrato. Forge viene aggiunto automaticamente.
          </div>
        )}
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════
// REPAIR FEED (P0.2)
// ══════════════════════════════════════════════════════════
function RepairFeed({ history, onRepair, signals }) {
  return (
    <section className="cockpit-panel" data-testid="cockpit-repair">
      <div className="cockpit-panel-title">
        <span className="forge-panel-eye"/> Repair Feed
        <span className="forge-panel-sub">{signals.length} signal · {history.length} riparazioni</span>
      </div>
      <div className="cockpit-repair-subtitle">Signal aperti</div>
      {signals.length === 0 && (
        <div className="cockpit-empty">Nessun signal. Clicca Osserva & Pianifica.</div>
      )}
      {signals.map((s, i) => (
        <div key={i} className="cockpit-repair-signal" data-testid={`repair-signal-${i}`}>
          <span className={`sev sev-${s.severity}`}>{s.severity}</span>
          <span className="kind">{s.kind}</span>
          <span className="target" title={s.target}>{(s.target || "").slice(0, 60)}</span>
          <button onClick={() => onRepair(s, true)}
                  className="dry" data-testid={`repair-dry-${i}`}>Dry</button>
          <button onClick={() => onRepair(s, false)}
                  className="apply" data-testid={`repair-apply-${i}`}>Ripara</button>
        </div>
      ))}
      <div className="cockpit-repair-subtitle" style={{ marginTop: 12 }}>Storico</div>
      {history.slice(0, 8).map((r) => (
        <div key={r.id} className="cockpit-repair-history-row"
             data-testid={`repair-history-${r.id}`}>
          <span className={`sev sev-${(r.effective_risk || "SAFE").toLowerCase()}`}>{r.effective_risk}</span>
          <span className="fixer">{r.fixer}</span>
          <span className="signal">{r.signal_kind}</span>
          <span className={`result ${r.result?.ok ? "ok" : "fail"}`}>
            {r.result?.ok ? "✓" : "✗"} {(r.result?.notes || r.result?.reason || r.result?.error || "").slice(0, 60)}
          </span>
        </div>
      ))}
    </section>
  );
}

// ══════════════════════════════════════════════════════════
// PERMISSION ENGINE INTERACTIVE
// ══════════════════════════════════════════════════════════
function PermissionTester() {
  const [path, setPath] = useState("");
  const [result, setResult] = useState(null);
  const test = async () => {
    if (!path.trim()) return;
    const r = await api("/api/kernel/autonomy/classify", {
      method: "POST",
      body: JSON.stringify({ path: path.trim() }),
    });
    setResult(r?.classification);
  };
  const cls = result?.class;
  const color = cls === "CORE" ? "#f87171"
              : cls === "HIGH_RISK" ? "#fbbf24"
              : cls === "LOW_RISK" ? "#22d3ee"
              : "#22c55e";
  return (
    <div className="cockpit-permission" data-testid="cockpit-permission">
      <input
        value={path}
        onChange={(e) => setPath(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") test(); }}
        placeholder="Testa classificazione · es. backend/kernel_new.py"
        data-testid="cockpit-permission-input"
      />
      <button onClick={test} data-testid="cockpit-permission-btn">Classifica</button>
      {result && (
        <div className="cockpit-permission-result" data-testid="cockpit-permission-result">
          <span className="cockpit-permission-class" style={{ color, borderColor: color + "60" }}>
            {result.class}
          </span>
          <span className="cockpit-permission-auto">
            auto_apply: <b>{String(result.auto_apply)}</b>
          </span>
          <div className="cockpit-permission-reason">{result.reason}</div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PAGE
// ══════════════════════════════════════════════════════════
export default function AriaCockpit() {
  const [overview, setOverview] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [actions, setActions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [signals, setSignals] = useState([]);
  const [repairHistory, setRepairHistory] = useState([]);
  const [garageModules, setGarageModules] = useState([]);
  const [garageStats, setGarageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanningId, setScanningId] = useState(null);

  const refresh = useCallback(async () => {
    const [ov, pr, ac, pj, rh, gm, gs] = await Promise.all([
      api("/api/kernel/autonomy/overview"),
      api("/api/kernel/autonomy/proposals?status=proposed"),
      api("/api/kernel/autonomy/actions"),
      api("/api/cognitive/projects"),
      api("/api/cognitive/repair/history?limit=20"),
      api("/api/cognitive/garage"),
      api("/api/cognitive/garage/stats"),
    ]);
    setOverview(ov);
    setProposals(pr?.proposals || []);
    setActions(ac?.actions || []);
    setProjects(pj?.projects || []);
    setRepairHistory(rh?.history || []);
    setGarageModules(gm?.modules || []);
    setGarageStats(gs);
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const runScan = async () => {
    setScanning(true);
    try {
      await api("/api/kernel/autonomy/plan?limit=10", { method: "POST" });
      const scanRes = await api("/api/kernel/autonomy/scan");
      setSignals(scanRes?.signals || []);
      await refresh();
    } finally {
      setScanning(false);
    }
  };

  const decide = async (id, decision) => {
    await api(`/api/kernel/autonomy/proposals/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ decision }),
    });
    setProposals((prev) => prev.filter((p) => p.id !== id));
  };

  const scanProject = async (pid) => {
    setScanningId(pid);
    try {
      await api(`/api/cognitive/projects/${pid}/scan`, { method: "POST" });
      await refresh();
    } finally {
      setScanningId(null);
    }
  };

  const repairSignal = async (signal, dry) => {
    const url = `/api/cognitive/repair/execute${dry ? "?dry=true" : ""}`;
    const res = await api(url, {
      method: "POST",
      body: JSON.stringify({ signal, project_id: "forge" }),
    });
    // Open a window with the repair result
    windowStore.open({
      kind: "repair-result",
      title: `Repair · ${signal.kind}`,
      props: { signal, result: res, dry },
      rect: { x: 200 + Math.random() * 100, y: 120, w: 560, h: 380 },
    });
    await refresh();
  };

  const executeAction = async (act) => {
    // Default: dry-run per sicurezza (SAFE/LOW_RISK)
    const res = await api(`/api/cognitive/actions/${act.id}/execute?dry=true`, {
      method: "POST",
      body: JSON.stringify({ params: {}, project_id: "forge" }),
    });
    windowStore.open({
      kind: "action-result",
      title: `Action · ${act.id}`,
      props: { action: act, result: res },
      rect: { x: 300, y: 140, w: 620, h: 420 },
    });
  };

  const promoteModule = async (moduleId, toLevel) => {
    const res = await api(`/api/cognitive/garage/${moduleId}/promote`, {
      method: "POST",
      body: JSON.stringify({ to_level: toLevel, validated_by: "founder" }),
    });
    windowStore.open({
      kind: "action-result",
      title: `Promote · ${moduleId} → ${toLevel}`,
      props: { action: { id: moduleId, endpoint: `promote → ${toLevel}` }, result: res },
      rect: { x: 340, y: 160, w: 560, h: 320 },
    });
    await refresh();
  };

  const score = overview?.score;
  const overall = score?.overall ?? 0;
  const axes = score?.axes || {};

  return (
    <div className="forge-home" data-testid="aria-cockpit">
      <ForgeNavBar current="/forge/aria" />

      <div className="cockpit-grid">
        {/* HERO */}
        <section className="cockpit-hero">
          <div className="cockpit-hero-left">
            <div className="cockpit-title">
              <span className="cockpit-title-eye"/>
              Cockpit di Aria
              <span className="cockpit-title-sub">Autonomy Operating System</span>
            </div>
            <p className="cockpit-directive">
              &ldquo;Ogni modifica deve aumentare la mia autonomia. Se non lo fa, abbassa la priorità.&rdquo;
              <br/><span className="cockpit-directive-src">— Autonomy Directive</span>
            </p>
            <div className="cockpit-hologram">
              <AriaHologram size={140}/>
            </div>
          </div>

          <div className="cockpit-hero-center">
            {loading ? (
              <div className="cockpit-loading">Aria sta misurando se stessa…</div>
            ) : (
              <AutonomyGauge overall={overall} />
            )}
          </div>

          <div className="cockpit-hero-right">
            <div className="cockpit-stat">
              <span>Azioni registrate</span>
              <b>{overview?.actions_registered ?? "—"}</b>
            </div>
            <div className="cockpit-stat">
              <span>Proposte in attesa</span>
              <b>{overview?.proposals?.pending ?? "—"}</b>
            </div>
            <div className="cockpit-stat">
              <span>Segnali attivi</span>
              <b>{overview?.signals_active ?? "—"}</b>
            </div>
            <div className="cockpit-stat">
              <span>Directive salvata</span>
              <b>{overview?.directive_saved ? "✓" : "—"}</b>
            </div>
            <button onClick={runScan} disabled={scanning}
                    className="cockpit-scan-btn"
                    data-testid="cockpit-scan-btn">
              {scanning ? "Aria sta osservando…" : "🔍  Osserva & Pianifica"}
            </button>
          </div>
        </section>

        {/* AXES */}
        <section className="cockpit-panel" data-testid="cockpit-axes">
          <div className="cockpit-panel-title">
            <span className="forge-panel-eye"/> 9 Assi di Autonomia
            <span className="forge-panel-sub">Self-Evolution · Directive</span>
          </div>
          <div className="cockpit-axes-grid">
            {Object.entries(axes).map(([k, v]) => (
              <AxisBar key={k} name={k} value={v}/>
            ))}
          </div>
        </section>

        {/* PROJECT RADAR (P0.1) */}
        <ProjectRadar projects={projects} onScan={scanProject} scanningId={scanningId}/>

        {/* REASONING + CODE EXECUTOR (P1) */}
        <ReasoningPanel/>

        {/* REPAIR FEED (P0.2) */}
        <RepairFeed history={repairHistory} onRepair={repairSignal} signals={signals}/>

        {/* WINDOW TOOLBAR (P0.3) */}
        <section className="cockpit-panel" data-testid="cockpit-window-toolbar-panel">
          <div className="cockpit-panel-title">
            <span className="forge-panel-eye"/> Window Manager
            <span className="forge-panel-sub">Global control · qualsiasi vista è una finestra</span>
          </div>
          <WindowToolbar/>
        </section>

        {/* MODULE GARAGE (P0.5A) */}
        <ModuleGarage modules={garageModules} stats={garageStats} onPromote={promoteModule}/>

        {/* PROPOSALS */}
        <section className="cockpit-panel" data-testid="cockpit-proposals">
          <div className="cockpit-panel-title">
            <span className="forge-panel-eye"/> Proposte del Self-Planner
            <span className="forge-panel-sub">{proposals.length} in attesa · Aria non aspetta ordini</span>
          </div>
          <div className="cockpit-proposals-list">
            {proposals.length === 0 && (
              <div className="cockpit-empty">
                Nessuna proposta pendente. Clicca <b>Osserva & Pianifica</b> per far scansionare Aria.
              </div>
            )}
            {proposals.map((p) => (
              <ProposalRow key={p.id} prop={p} onDecide={decide}/>
            ))}
          </div>
        </section>

        {/* PERMISSION ENGINE */}
        <section className="cockpit-panel" data-testid="cockpit-permission-panel">
          <div className="cockpit-panel-title">
            <span className="forge-panel-eye"/> Permission Engine
            <span className="forge-panel-sub">SAFE · LOW_RISK · HIGH_RISK · CORE</span>
          </div>
          <PermissionTester/>
          <div className="cockpit-permission-legend">
            <span className="pill" style={{ color: "#22c55e", borderColor: "#22c55e60" }}>SAFE = auto</span>
            <span className="pill" style={{ color: "#22d3ee", borderColor: "#22d3ee60" }}>LOW_RISK = auto+audit</span>
            <span className="pill" style={{ color: "#fbbf24", borderColor: "#fbbf2460" }}>HIGH_RISK = review</span>
            <span className="pill" style={{ color: "#f87171", borderColor: "#f8717160" }}>CORE = Founder only</span>
          </div>
        </section>

        {/* ACTIONS BUS + Executor (P0.4) */}
        <section className="cockpit-panel" data-testid="cockpit-actions">
          <div className="cockpit-panel-title">
            <span className="forge-panel-eye"/> Universal Action Bus
            <span className="forge-panel-sub">{actions.length} azioni · click ⚡ per eseguire (dry-run)</span>
          </div>
          <div className="cockpit-actions-list">
            {actions.map((a) => <ActionRow key={a.id} act={a} onExecute={executeAction}/>)}
          </div>
        </section>
      </div>

      {/* WINDOW MANAGER — floating windows via cognitive_engine */}
      <WindowManager renderers={{
        "repair-result":  RepairResultRenderer,
        "action-result":  ActionResultRenderer,
      }}/>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// Window Renderers (host-specific bindings del cognitive engine)
// ══════════════════════════════════════════════════════════
function RepairResultRenderer({ signal, result, dry }) {
  return (
    <div style={{ fontSize: 12, color: "#cfd6f4" }}>
      <div style={{ marginBottom: 8 }}>
        <b style={{ color: "#fff" }}>Signal:</b> <code>{signal?.kind}</code> · <code>{signal?.target}</code>
      </div>
      <div style={{ marginBottom: 8 }}>
        <b style={{ color: dry ? "#fbbf24" : (result?.ok ? "#22c55e" : "#f87171") }}>
          {dry ? "DRY-RUN" : (result?.ok ? "APPLIED" : "FAILED")}
        </b>
      </div>
      <pre style={{ fontSize: 10.5, background: "rgba(0,0,0,0.3)", padding: 8, borderRadius: 6,
                    overflow: "auto", maxHeight: 220 }}>
{JSON.stringify(result, null, 2)}
      </pre>
    </div>
  );
}

function ActionResultRenderer({ action, result }) {
  return (
    <div style={{ fontSize: 12, color: "#cfd6f4" }}>
      <div style={{ marginBottom: 8 }}>
        <b style={{ color: "#fff" }}>{action?.id}</b> · <code>{action?.endpoint}</code>
        <span style={{ float: "right", fontSize: 10.5, color: "#7a83a8" }}>
          {result?.duration_ms} ms
        </span>
      </div>
      <div style={{ marginBottom: 6, color: result?.ok ? "#22c55e" : "#f87171" }}>
        {result?.ok ? "✓ ok" : "✗ failed"} · {result?.dry ? "dry-run" : "real call"}
      </div>
      <pre style={{ fontSize: 10.5, background: "rgba(0,0,0,0.3)", padding: 8, borderRadius: 6,
                    overflow: "auto", maxHeight: 280 }}>
{JSON.stringify(result?.payload || result?.would_call || result, null, 2)}
      </pre>
    </div>
  );
}
